import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LeadFormDialog, LABEL_COLOR } from "@/components/admin/LeadFormDialog";
import { Plus, BellRing, CalendarDays, Users, Receipt, ClipboardList, PhoneCall } from "lucide-react";

function useLeadsSynced(cb) { useEffect(() => { window.addEventListener("leads-synced", cb); return () => window.removeEventListener("leads-synced", cb); }, [cb]); }

export default function StaffDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [trips, setTrips] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/staff/me/dashboard").then(({ data }) => setD(data)).catch(() => {});
  useLeadsSynced(load);
  useEffect(() => { load(); api.get("/trips").then(({ data }) => setTrips(data)); }, []);
  if (!d) return <p className="text-muted-foreground">Loading…</p>;

  const cards = [
    ["Today's Leads", d.today_leads], ["Total Leads", d.total_leads], ["Follow-ups Due", d.followups_due, d.followups_due > 0 ? "text-red-600 dark:text-red-400" : ""],
    ["Upcoming Follow-ups", d.upcoming_followups.length], ["Today's Bookings", d.today_bookings], ["Upcoming Bookings", d.upcoming_bookings],
    ["Total Bookings", d.total_bookings], ["Invoices", d.invoices], ["Converted", d.converted, "text-emerald-600 dark:text-emerald-400"],
  ];
  return (
    <div className="space-y-6" data-testid="staff-dashboard">
      <div><h1 className="font-heading text-3xl sm:text-4xl font-semibold">Hi {user?.name?.split(" ")[0]} 👋</h1><p className="text-muted-foreground text-sm">{user?.employee_code} · {user?.job_title || "Team"}</p></div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="quick-actions">
        <Button className="h-14 rounded-2xl justify-start text-base" onClick={() => { setEditing(null); setOpen(true); }} data-testid="qa-add-lead"><Plus className="h-5 w-5 mr-2" /> Add Lead</Button>
        <Link to="/admin/leads?due=1"><Button variant="outline" className="h-14 w-full rounded-2xl justify-start text-base" data-testid="qa-followups"><PhoneCall className="h-5 w-5 mr-2" /> Follow-ups</Button></Link>
        <Link to="/admin/leads"><Button variant="outline" className="h-14 w-full rounded-2xl justify-start text-base"><ClipboardList className="h-5 w-5 mr-2" /> My Leads</Button></Link>
        <Link to="/admin/bookings"><Button variant="outline" className="h-14 w-full rounded-2xl justify-start text-base"><Users className="h-5 w-5 mr-2" /> Bookings</Button></Link>
        <Link to="/admin/invoices"><Button variant="outline" className="h-14 w-full rounded-2xl justify-start text-base"><Receipt className="h-5 w-5 mr-2" /> Invoices</Button></Link>
        <Link to="/admin/calendar"><Button variant="outline" className="h-14 w-full rounded-2xl justify-start text-base"><CalendarDays className="h-5 w-5 mr-2" /> Calendar</Button></Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {cards.map(([l, v, c]) => <div key={l} className="rounded-xl border bg-card p-4" data-testid="stat-card"><p className="text-xs text-muted-foreground">{l}</p><p className={`font-heading text-3xl ${c || ""}`}>{v}</p></div>)}
      </div>

      <div className="rounded-xl border bg-card p-4">
        <p className="font-medium flex items-center gap-2 mb-3"><BellRing className="h-4 w-4 text-amber-500" /> Follow-ups due ({d.due_list.length})</p>
        <div className="space-y-2" data-testid="due-list">
          {d.due_list.map((l) => (
            <button key={l.id} type="button" onClick={() => { setEditing(l); setOpen(true); }} className="w-full text-left rounded-lg border p-3 hover:bg-muted/40 flex flex-wrap items-center justify-between gap-2" data-testid="due-item">
              <div><p className="font-medium">{l.name} <span className="text-muted-foreground font-normal">— {l.destination || "—"} — {l.pax} Pax</span></p><p className="text-xs text-muted-foreground">Due {l.next_followup} · {l.whatsapp}</p></div>
              <Badge variant="secondary" className={`rounded-full ${LABEL_COLOR[l.label] || ""}`}>{l.label}</Badge>
            </button>
          ))}
          {!d.due_list.length && <p className="text-sm text-muted-foreground">Nothing due. 🎉</p>}
        </div>
      </div>
      <LeadFormDialog open={open} onOpenChange={setOpen} lead={editing} labels={d.labels} trips={trips} onSaved={load} />
    </div>
  );
}
